// Market Watch Constants

// Fixed storage key for filter persistence (decoupled from dynamic sheet names)
export const MARKET_WATCH_SHEET = "market_watch_filters";

/**
 * @deprecated The backend now generates its own prompt with pre-computed rule
 * results, dynamic thresholds, and pivot data.  This constant is retained only
 * for backwards-compatibility; no frontend code should reference it.
 */
export const MARKET_WATCH_ANALYSIS_PROMPT = `
## DECISION RULES

### Available Columns in Data

| Column | Description |
|--------|-------------|
| target_asin | Product identifier - analyze each separately |
| global_price_cluster_label | Price range segment |
| global_cluster_feature_label | Feature segment |
| adjusted_purchase_vol_forecast | Total market demand in segment |
| asin_purchase_volume_forecast | Target's sales in segment |
| Demand Share % | Calculated: (sum of asin_purchase_volume_forecast / sum of adjusted_purchase_vol_forecast) × 100 |

### Pre-Analysis: Map Position vs Demand

Before applying rules, compute these for EACH target_asin:

1. **Merchant's Home Segments** — segments where Demand Share % > 0
   - Home Price Cluster = global_price_cluster_label with highest Demand Share %
   - Home Feature Cluster = global_cluster_feature_label with highest Demand Share %

2. **Top Demand Segments** — segments with highest adjusted_purchase_vol_forecast
   - Top Demand Price Cluster = global_price_cluster_label with highest adjusted_purchase_vol_forecast
   - Top Demand Feature Cluster = global_cluster_feature_label with highest adjusted_purchase_vol_forecast

3. **Mismatch Detection**
   - Price Mismatch = Home Price Cluster ≠ Top Demand Price Cluster
   - Feature Mismatch = Home Feature Cluster ≠ Top Demand Feature Cluster

### Decision Logic

For EACH target_asin, evaluate rules in priority order. **First match wins — stop checking after a match.**

| Priority | Recommendation | Condition |
|----------|----------------|-----------|
| 1 | **DEFEND MARKET SHARE** | Demand Share % >= 15 in any global_price_cluster_label or global_cluster_feature_label AND that segment has adjusted_purchase_vol_forecast > 30 |
| 2 | **UPDATE LISTING** | Demand Share % > 5 in a global_price_cluster_label BUT Demand Share % = 0 in a global_cluster_feature_label **within that same price cluster** where adjusted_purchase_vol_forecast > 30 |
| 3 | **ADVERTISE MORE** | Demand Share % between 1-15 in a global_price_cluster_label with adjusted_purchase_vol_forecast > 50 AND Demand Share % > 0 in at least one global_cluster_feature_label **within that same price cluster** (merchant is in right price AND right feature, just low share) |
| 4 | **CHANGE PRICE** | Demand Share % = 0 in global_price_cluster_label with highest adjusted_purchase_vol_forecast AND Demand Share % > 0 in a **different** global_price_cluster_label (merchant sells, but at wrong price) AND a similar global_cluster_feature_label exists in both the merchant's active price cluster and the high-demand price cluster (feature overlap confirms it's a price problem, not a product problem) |
| 5 | **CREATE NEW PRODUCT** | Demand Share % = 0 in BOTH the highest-demand global_price_cluster_label AND highest-demand global_cluster_feature_label AND adjusted_purchase_vol_forecast > 100 in that segment |

---

### Output Requirements

For EACH target_asin, provide a **detailed analysis** covering ALL of the following:

#### 1. Current Position
Which global_price_cluster_label has the highest Demand Share % (merchant's home price segment)
Which global_cluster_feature_label has the highest Demand Share % (merchant's home feature segment)
The merchant's Demand Share % values in those home segments

#### 2. Price Cluster Analysis — ALL Price Gaps
Which price cluster has the highest adjusted_purchase_vol_forecast overall (top demand price cluster)
Merchant's Demand Share % in that top-demand price cluster
**List ALL price cluster gaps**: Report EVERY global_price_cluster_label where the merchant has Demand Share % = 0 AND adjusted_purchase_vol_forecast > 30. Order them by adjusted_purchase_vol_forecast descending. For EACH gap include:
  - The price cluster name
  - Its adjusted_purchase_vol_forecast (total demand)
  - Estimated opportunity = adjusted_purchase_vol_forecast × 20%
Do NOT report only the single largest gap — report ALL qualifying gaps

#### 3. Feature Cluster Analysis — ALL Feature Gaps
Which feature cluster has the highest adjusted_purchase_vol_forecast **within the merchant's active price cluster**
Merchant's Demand Share % in that feature cluster
**List ALL feature cluster gaps**: Report EVERY global_cluster_feature_label where the merchant has Demand Share % = 0 AND adjusted_purchase_vol_forecast > 30 **within the merchant's active price cluster**. Order them by adjusted_purchase_vol_forecast descending. For EACH gap include:
  - The feature cluster name (its label describes what keywords/features to add to the listing)
  - Its adjusted_purchase_vol_forecast (total demand)
  - Estimated opportunity = adjusted_purchase_vol_forecast × 20%
Do NOT report only the single largest gap — report ALL qualifying gaps

#### 4. Recommendation
Provide recommendations from the 5 options above:
State ONLY the recommendation name (e.g., "UPDATE FEATURE MESSAGING", "ADVERTISE MORE")
Do NOT include rule numbers, priority numbers, or references like "Rule 1", "Rule 2", "Priority 3" anywhere in the output
List the specific segment names and demand numbers that triggered the recommendation
Show the estimated opportunity breakdown:
  - For EACH gap segment: uncaptured weekly units = adjusted_purchase_vol_forecast × 20%
  - Then show the **combined total** opportunity across ALL gaps
`;

// Unified color theme matching taskpane (green-based)
export const THEME = {
  primary: "#068E75",    
  primaryDark: "#065F46",   
  primaryLight: "#D1FAE5",
  headerBg: "#068E75",
  headerText: "#FFFFFF",
  sectionBg: "#E8F5F0",
  sectionText: "#065F46",
  cardBg: "#F0FDF9",
  cardText: "#065F46",
  cardBorder: "#A7F3D0",
  tableBorder: "#D1FAE5",
};

// ASIN group header banner (subtle grey)
export const ASIN_HEADER_THEME = {
  bg: "#F1F5F9",
  text: "#1E293B",
  border: "#CBD5E1",
};

// Uniform KPI theme (all cards same style)
export const KPI_UNIFORM_THEME = {
  header: THEME.primary,
  headerText: THEME.headerText,
  body: THEME.cardBg,
  bodyText: THEME.primaryDark,
  border: THEME.cardBorder,
};

// Strategy row highlighting colors for flattened grid
export const STRATEGY_HIGHLIGHT = {
  rank1: "#A8C7FA",  // Medium blue — primary growth strategy
  rank2: "#B7E1C1",  // Medium green — secondary growth strategy
  qualifying: "#F3F4F6",  // Light gray — qualifying, ranked 3rd+
};
